/**
 * Shared alert helpers — palette derivation, message cascades, template
 * vars, filter logic. Used by AlertBoxWidget and FullscreenAlertsWidget.
 *
 * Behavior is frozen — the AlertBoxWidget refactor that introduced this
 * file is a pure move; tests should still pass against either widget.
 */

import type { StreamEvent, Platform } from '@hypevault/shared-types';

// ──────────────────────────────────────────────────────────────────────
// Palette
// ──────────────────────────────────────────────────────────────────────

export interface AlertPalette {
  base: string;
  light: string;
  dark: string;
  dim: string;
}

export const PLATFORM_COLORS: Record<Platform, AlertPalette> = {
  hypevault: { base: '#f655a6', light: '#f988bf', dark: '#bf2e76', dim: 'rgba(246,85,166,0.4)' },
  twitch: { base: '#9146ff', light: '#a970ff', dark: '#6b2dc7', dim: 'rgba(145,70,255,0.4)' },
  youtube: { base: '#ff0000', light: '#ff4444', dark: '#cc0000', dim: 'rgba(255,0,0,0.4)' },
  kick: { base: '#53fc18', light: '#7dff4d', dark: '#3ac710', dim: 'rgba(83,252,24,0.4)' },
};

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / d + 2;
      break;
    default:
      h = (r - g) / d + 4;
  }
  return { h: h * 60, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const h6 = ((h % 360) + 360) % 360;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h6 < 60) [r, g, b] = [c, x, 0];
  else if (h6 < 120) [r, g, b] = [x, c, 0];
  else if (h6 < 180) [r, g, b] = [0, c, x];
  else if (h6 < 240) [r, g, b] = [0, x, c];
  else if (h6 < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function derivePalette(baseHex: string): AlertPalette {
  const rgb = hexToRgb(baseHex);
  if (!rgb) return PLATFORM_COLORS.hypevault;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return {
    base: baseHex,
    light: hslToHex(h, Math.min(1, s), Math.min(0.92, l + 0.1)),
    dark: hslToHex(h, s, Math.max(0.15, l - 0.18)),
    dim: `rgba(${rgb.r},${rgb.g},${rgb.b},0.4)`,
  };
}

// ──────────────────────────────────────────────────────────────────────
// Card rarity palette (mirrors CardAlertWidget). Used by Fullscreen
// when the event is a cardRedemption so particles + accent match rarity.
// ──────────────────────────────────────────────────────────────────────

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';

export const RARITY_COLORS: Record<Rarity, { base: string; glow: string; label: string }> = {
  common: { base: '#9ca3af', glow: 'rgba(156,163,175,0.55)', label: 'Common' },
  rare: { base: '#3b82f6', glow: 'rgba(59,130,246,0.6)', label: 'Rare' },
  epic: { base: '#a855f7', glow: 'rgba(168,85,247,0.65)', label: 'Epic' },
  legendary: { base: '#f59e0b', glow: 'rgba(245,158,11,0.7)', label: 'Legendary' },
  mythic: { base: '#ef4444', glow: 'rgba(239,68,68,0.75)', label: 'Mythic' },
};

export const RARITY_ORDER: Rarity[] = ['common', 'rare', 'epic', 'legendary', 'mythic'];

// ──────────────────────────────────────────────────────────────────────
// Message cascades
// ──────────────────────────────────────────────────────────────────────

export const DEFAULT_MESSAGES: Record<string, string> = {
  tip: 'Tipped {amount} coins!',
  tip_received: 'tipped {amount}',
  follow: 'Now following!',
  sub: 'Just subscribed!',
  'sub.gift': 'Gifted a sub!',
  'sub.yt-member': 'Became a member!',
  'sub.gift.yt-member': 'Gifted a membership!',
  'sub.kick-sub': 'Just subscribed on Kick!',
  'sub.gift.kick-sub': 'Gifted a Kick sub!',
  'sub.hypevault': 'Subscribed to the channel!',
  'sub.gift.hypevault': 'Gifted a HypeVault sub!',
  cheer: 'Cheered {amount} bits!',
  'cheer.kick': 'Sent {amount} Kicks!',
  raid: 'Raided with {viewers} viewers!',
  alertPurchase: 'Bought {title}!',
  cardRedemption: 'Redeemed {title}!',
  superChat: 'Sent a SuperChat for {amount}!',
  superSticker: 'Sent a Super Sticker!',
  channelPointRedemption: 'Redeemed {title} for {cost} channel points!',
};

export const EVENT_BADGES: Record<string, string> = {
  tip: '💜',
  tip_received: '🎉',
  follow: '⭐',
  sub: '🎉',
  'sub.gift': '🎁',
  'sub.yt-member': '⭐',
  'sub.gift.yt-member': '🎁',
  'sub.kick-sub': '🎉',
  'sub.gift.kick-sub': '🎁',
  'sub.hypevault': '💖',
  'sub.gift.hypevault': '🎁',
  cheer: '💎',
  'cheer.kick': '💚',
  raid: '🚀',
  alertPurchase: '🎁',
  cardRedemption: '🃏',
  superChat: '💰',
  superSticker: '🌟',
  channelPointRedemption: '⭐',
};

/**
 * Returns the cascade of message/badge keys for an event, most specific
 * first. Lookup walks the list and uses the first match.
 */
export function eventKeyCascade(e: StreamEvent): string[] {
  if (e.type === 'sub') {
    const giftPart = e.isGift ? '.gift' : '';
    const keys = [`sub${giftPart}.${e.tier}`];
    if (e.isGift) keys.push('sub.gift');
    keys.push('sub');
    return keys;
  }
  if (e.type === 'cheer' && e.platform === 'kick') return ['cheer.kick', 'cheer'];
  return [e.type];
}

export function eventToken(e: StreamEvent): string {
  return eventKeyCascade(e)[0]!;
}

export function pickFrom<T>(cascade: string[], ...maps: Record<string, T>[]): T | undefined {
  for (const map of maps) {
    for (const key of cascade) {
      if (map[key] != null) return map[key];
    }
  }
  return undefined;
}

export function extractUsername(e: StreamEvent): string {
  switch (e.type) {
    case 'raid':
      return e.raiderName;
    case 'sub':
      return e.isGift && e.gifterName ? e.gifterName : e.viewerName;
    default:
      return (e as { viewerName?: string }).viewerName ?? 'Someone';
  }
}

export function extractTemplateVars(e: StreamEvent): Record<string, string> {
  const vars: Record<string, string> = { user: extractUsername(e) };
  switch (e.type) {
    case 'tip':
      vars.amount = e.coins.toLocaleString();
      break;
    case 'tip_received':
      vars.amount = `$${(e.amountCents / 100).toFixed(2)}`;
      break;
    case 'cheer':
      vars.amount = e.bits.toLocaleString();
      break;
    case 'sub':
      vars.tier = e.tier;
      vars.months = String(e.months);
      break;
    case 'raid':
      vars.viewers = String(e.viewers);
      break;
    case 'alertPurchase':
      vars.title = e.productName;
      vars.amount = e.coins.toLocaleString();
      break;
    case 'cardRedemption':
      vars.title = e.cardName;
      break;
    case 'superChat':
    case 'superSticker':
      vars.amount = `$${(e.amountCents / 100).toFixed(2)}`;
      break;
    case 'channelPointRedemption':
      vars.title = e.rewardTitle;
      vars.cost = e.rewardCost.toLocaleString();
      if (e.userInput) vars.input = e.userInput;
      break;
  }
  return vars;
}

export function renderTemplate(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
}

/**
 * Free-form viewer-supplied note attached to an event (tip message,
 * cheer comment, SuperChat text). Surfaced as a secondary line in the
 * alert below the templated main message.
 */
export function extractSubMessage(e: StreamEvent): string | null {
  switch (e.type) {
    case 'tip':
    case 'tip_received':
    case 'cheer':
    case 'superChat':
      return e.message?.trim() || null;
    default:
      return null;
  }
}

export function passesMinFilters(
  e: StreamEvent,
  minTip: number,
  minCheer: number,
  minSuperCents: number,
): boolean {
  if (e.type === 'tip' && e.coins < minTip) return false;
  // `minTipCoins` is reused as a cents threshold for real-money tips so a
  // single widget setting can gate both legacy + Stripe tips.
  if (e.type === 'tip_received' && e.amountCents < minTip) return false;
  if (e.type === 'cheer' && e.bits < minCheer) return false;
  if ((e.type === 'superChat' || e.type === 'superSticker') && e.amountCents < minSuperCents)
    return false;
  return true;
}
